/**
 * Route-aware default facet filters for the medical PA orders worklist.
 *
 * Some worklist URLs imply an always-on filter that the user can't
 * remove via the filter panel — e.g. `/worklists/orders-to-process`
 * is intrinsically scoped to `master_auth_status: new`.
 * This hook returns those route-derived defaults so they can be merged
 * into every search payload (initial fetch, filter/search change, and
 * next-page pagination) without polluting the user's actual filter
 * selections in Redux.
 *
 * @module useDefaultFacetFiltersForRoute
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { MedicalPaFilterKeysToFetchFromPostgres } from "../../../../enums/medicalPAFilterKeys";

/** URL slug that maps to the orders-to-process worklist route. */
const ORDERS_TO_PROCESS_ROUTE_SUFFIX = "/orders-to-process";

/** master_auth_status value the orders-to-process route is scoped to. */
const ORDERS_TO_PROCESS_MASTER_AUTH_STATUS = "new";

const ORDERS_TO_PROCESS_DEFAULT_FACETS: Record<string, string[]> = {
  [MedicalPaFilterKeysToFetchFromPostgres.MasterAuthStatus]: [
    ORDERS_TO_PROCESS_MASTER_AUTH_STATUS,
  ],
};

/**
 * Returns facet filters that should be merged into every medical PA
 * orders search payload based purely on the current URL.
 *
 * @returns A facet-filter map keyed by Postgres facet name. Empty
 *   object when no route-level defaults apply.
 */
export const useDefaultFacetFiltersForRoute = (): Record<string, string[]> => {
  const { pathname } = useLocation();
  return useMemo(() => {
    if (pathname.endsWith(ORDERS_TO_PROCESS_ROUTE_SUFFIX)) {
      return ORDERS_TO_PROCESS_DEFAULT_FACETS;
    }
    return {};
  }, [pathname]);
};
